import { Injectable } from '@angular/core';
import {
  Subscription,
  Subject,
  combineLatest,
  BehaviorSubject,
  Unsubscribable,
  from
} from 'rxjs';
import { map, tap, filter, debounceTime } from 'rxjs/operators';
import { filter as filterL, forEach, map as mapL, maxBy } from 'lodash';
import { ListenersAggregate } from './listeners.aggregate';
import {
  ValuesExchangeProvider,
  ITimestampedUpdate,
  NO_VALUE
} from './values.exchange.provider';

interface IExchangeTopic {
  producer: Subject<any>;
  unsubscribeFromProducer: Subscription;
  interestedListeners: ListenersAggregate<any>;
}

@Injectable({
  providedIn: 'root'
})
export class ValuesExchangeProviderImpl extends ValuesExchangeProvider {
  _topics: Map<string, IExchangeTopic> = new Map();

  constructor() {
    super();
  }

  private createTopicGroup(stateless: boolean): IExchangeTopic {
    return {
      producer: null,
      unsubscribeFromProducer: null,
      interestedListeners: new ListenersAggregate(stateless)
    };
  }

  makeKey(topic, stateless: boolean) {
    return `${stateless ? 'less' : 'full'}__${topic}`;
  }

  getTopic(topic: string, stateless: boolean): IExchangeTopic {
    const key = this.makeKey(topic, stateless);
    return this._topics.get(key);
  }

  setTopic(topic: string, stateless: boolean, group: IExchangeTopic) {
    const key = this.makeKey(topic, stateless);
    this._topics.set(key, group);
  }

  getCreateTopicGroup(topic: string, stateless: boolean): IExchangeTopic {
    let topicGroup: IExchangeTopic = this.getTopic(topic, stateless);
    if (!topicGroup) {
      this.setTopic(
        topic,
        stateless,
        (topicGroup = this.createTopicGroup(stateless))
      );
    }
    return topicGroup;
  }

  public registerMultipleInterests(
    topics: string[],
    subscriber: any,
    stateless?: boolean
  ): Subscription {
    const multiSubjects = [];
    forEach(topics, (topic: string) => {
      const subj: ListenersAggregate<any> = this.getCreateTopicGroup(
        topic,
        stateless
      ).interestedListeners;
      multiSubjects.push(subj.listeners);
    });
    return combineLatest(multiSubjects)
      .pipe(
        // do not fire an update to listeners, if this is 'partial' update
        // see comments to ITimestampedUpdate for 'partial' and 'full' updates
        filter((input: ITimestampedUpdate[]) => {
          return isFullUpdate(input);
        }),
        map((values: ITimestampedUpdate[]) => {
          const result = new Map();
          for (let index = 0; index < values.length; index++) {
            const topicName = topics[index];
            // get a rid of vexTimestamp, which is used only internally by this class
            let topicValue = null;
            if (values[index].vexTimestamp) {
              topicValue = values[index].rawValue;
            } else {
              topicValue = values[index];
            }
            result.set(topicName, topicValue);
          }
          return result;
        }),
        // in case you are interested to receive a Map of values from multiple producers at once,
        // in some scenarios it might be difficult to achieve:
        // - producers acquire the value one by one, then are calling .next() method to publish produced value;
        // The next() method of BehaviorSubject is a synchronous method - meaning your listeners are to be notified
        // immediately.
        // As result, the Map of values has correct keys - but some values will be total garbage (as their producers
        // haven't finished acquiring the right values to be published).
        // The filter below allows to prevent notification from happening, if at least one value is NO_VALUE object.
        // In case this change breaks some logic on your side (eg, you want to deal with undefined values yourself),
        // then make sure your producers are sending out a simple null (eg, produce.next(null)) or any other value,
        // different from NO_VALUE object.
        filter((nextValues: any) => {
          for (const value of Array.from(nextValues.values())) {
            if (value === NO_VALUE) {
              return false;
            }
          }
          return true;
        }),
        //Let update layout (show/hide) before notifying
        debounceTime(0)
      )
      .subscribe(subscriber);
  }

  public registerInterest(
    topic: string,
    subscriber: any,
    stateless?: boolean
  ): Unsubscribable {
    // previous implementations of registerInterest were not consistent with behavior of 'registerMultipleInterests'.
    // both methods should ignore NO_VALUE (ie, do not notify listeners if value is NO_VALUE) and
    // both methods should suppress notification if there is no change between previous and latest values.
    const subj: ListenersAggregate<any> = this.getCreateTopicGroup(
      topic,
      stateless
    ).interestedListeners;

    return from(subj.listeners)
      .pipe(
        // do NOT let through an update, if we determine the cause of event is 'partial' update.
        // see comments to ITimestampedUpdate for explanation of 'partial' and 'full' update and why it matters.
        filter(input => {
          return isFullUpdate([input]);
        }),
        // unwrap raw value from ITimestampedUpdate-like data structure, if we determine it was wrapped this way.
        map(values => {
          if (values && values.vexTimestamp) {
            return values.rawValue;
          }
          return values;
        }),
        // see comments to similar filter function on registerMultipleInterests(). The need is to suppress NO_VALUE
        // notifications.
        filter((nextValue: any) => {
          return nextValue !== NO_VALUE;
        })
      )
      .subscribe(subscriber);
  }

  public registerOutput(
    topic: string,
    producer?: Subject<any>,
    stateless?: boolean
  ): Subject<any> {
    if (!producer) {
      producer = stateless
        ? new Subject<any>()
        : new BehaviorSubject<any>(null);
    }
    let topicGroup = this.getTopic(topic, stateless);
    if (topicGroup) {
      if (topicGroup.producer) {
        topicGroup.unsubscribeFromProducer.unsubscribe();
      }
      topicGroup.producer = producer;
    } else {
      this.setTopic(
        topic,
        stateless,
        (topicGroup = this.createTopicGroup(stateless))
      );
      topicGroup.producer = producer;
    }
    const interest: ListenersAggregate<any> = topicGroup.interestedListeners;

    // notice special logic in map function below.
    // the function wraps a RAW value from producer into simple data structure and adds a timestamp.
    // this data structure will be passed to listeners, who registered via 'registerInterest' and
    // 'registerMultipleInterest'.
    topicGroup.unsubscribeFromProducer = producer
      .pipe(
        tap(() => {
          // nothing to be done. left here for your convenience to debug
        }),
        map((input: any) => {
          return {
            rawValue: input,
            vexTimestamp: Date.now()
          };
        })
      )
      //  .subscribe(interest.next.bind(interest)); If you want to eliminate use of ListenersAggregate, make sure to bind producer to interest.next() method!
      .subscribe(interest.nextValue.bind(interest));
    return producer;
  }

  public unregisterOutput(
    topic: string,
    producer?: Subject<any>,
    stateless?: boolean
  ) {
    const topicGroup: IExchangeTopic = this.getTopic(topic, stateless);
    if (topicGroup) {
      if (topicGroup.producer === producer) {
        topicGroup.unsubscribeFromProducer.unsubscribe();
        topicGroup.producer = null;
      } else if (!producer) {
        // enforce unregistration without checking producer match
        if (topicGroup.unsubscribeFromProducer) {
          topicGroup.unsubscribeFromProducer.unsubscribe();
        }
        topicGroup.producer = null;
      }
    }
  }

  public registerWithWidgetConfig(
    widgetConfig: any,
    callback: Function,
    stateless?: boolean
  ): Subscription {
    const inputs = mapL(
      filterL(widgetConfig.inputs, { ref: { object: 'output' } }),
      'ref.id'
    );
    if (inputs) {
      return this.registerMultipleInterests(inputs, callback, stateless);
    }
    return null;
    // the difference between registerInterest and registerMultipleInterests is huge: the multipleXX variant creates a map, and single
    // interest does not. This is confusing/when writing new queries. So lets stop using registerInterest by default/leave it to those
    // special cases where developer explicitly uses that registration method.
    // if (inputs && inputs.length === 1) {
    //   this.registerInterest(inputs[0], callback);
    // }
    // else if (inputs.length > 1) {
    //   this.registerMultipleInterests(inputs, callback);
    // }
  }

  /**
   * Is expected to be used by user-initiated activities (eg, activating context menu item)
   * Locates group of listeners, registered on certain topic, and notifies them with given value.
   * If topic has already existing producer, just calls next() on producer.
   * If topic has no producer yet, Subject producer is registered and value emitted.
   */
  public notifyListeners(topic: string, value: any, stateless?: boolean) {
    const topicGroup: IExchangeTopic = this.getTopic(topic, stateless);
    if (topicGroup && topicGroup.producer) {
      // very important! If you want to use one-time notifications only, then producer should be discarded
      // immediately after notification is emitted. Therefore if you have oneTimeAction and arrive to this
      // code, something is not consistent.
      if (stateless) {
        throw Error(
          'One time notification requested, but found a persistent producer! Topic:' +
            topic
        );
      }
      topicGroup.producer.next(value);
    } else {
      if (stateless) {
        const producer = new Subject<any>();
        this.registerOutput(topic, producer, stateless);
        producer.next(value);
        this.unregisterOutput(topic, producer, stateless);
      } else {
        const producer = new BehaviorSubject<any>(value);
        this.registerOutput(topic, producer, stateless);
        // upon subscription, all listeners were informed..
      }

      // producer.next(value);
    }
  }

  public getLastKnownValue(topic: string): any {
    // stateful topics only!
    const topicGroup: IExchangeTopic = this.getTopic(topic, false);
    if (!topicGroup || !topicGroup.producer) {
      return undefined;
    }
    if (!(topicGroup.producer instanceof BehaviorSubject)) {
      throw Error(
        'Topic was assumed to be based on stateful producer, and this is not the case!' +
          topic
      );
    }
    return (topicGroup.producer as BehaviorSubject<any>).value;
  }
}

/**
 * This method analyzes given array of values from multiple producers
 * and tries to figure out who is the most-recent trigger of update.
 * Then it checks if most-recent update is 'full' or 'partial'.
 * If it is 'full', then a bucket of values will passed over to listeners to react.
 * If it is 'partial', then listeners won't be notified.
 *
 * method HOPES the input will represent given type/structure.
 * but it takes necessary measures to handle other types of structures as well.
 * please note that it tries to 'err' on the side of 'full' update - ie, whenever
 * it can't figure out if it is partial or full update, it says 'it is full'.
 */
function isFullUpdate(input: ITimestampedUpdate[]): boolean {
  if (!input || input.length === 0) {
    return true;
  }
  const latestUpdate = maxBy(input, value => (value ? value.vexTimestamp : 0));
  if (!latestUpdate || !latestUpdate.vexTimestamp) {
    return true;
  }
  // in case raw value has no hint on update type, we assume this is full update.
  let result = false;
  for (const value of input) {
    if (!value) {
      result = true;
      continue;
    }
    // ignore properties that have been updated earlier (eg, you might have fired an update with a tip 'partial'
    // on  widget instance X. Once a full update comes in from a widget instance Y, you want to ignore your
    // partial update tip from widget instance X, since it is 'older' and no longer relevant.
    if (value.vexTimestamp !== latestUpdate.vexTimestamp) {
      continue;
    }
    if (!value.rawValue) {
      result = true;
      continue;
    }
    const updateHint = value.rawValue.update_type;
    if (updateHint !== 'partial') {
      result = true;
    }
  }
  return result;
}
